import { useRef, useState } from 'react';
import { useDismissOnOutsideClick } from '../../../hooks/useDismissOnOutsideClick';
import { useSortable } from '@dnd-kit/react/sortable';
import { useTagSuggestions, isBlankElement, TagFormElement } from '../../../hooks/useTagForm';
import type { Tag } from '../../../hooks/useProjectTags';
import { TagModal } from '../../../components/modals/TagModal';

interface Props {
  element: TagFormElement;
  index: number;
  projectId: string;
  onSave: (updated: TagFormElement) => void;
  onRemove: () => void;
}

interface TagRef { id: string; name: string; color: string; }

export function SortableElement({ element, index, projectId, onSave, onRemove }: Props) {
  const { ref, isDragging } = useSortable({ id: element.id ?? `new-${index}`, index });

  const [editMode, setEditMode] = useState(isBlankElement(element));
  const [showEditModal, setShowEditModal] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [selectedTag, setSelectedTag] = useState(element.tag ?? null);
  const [textName, setTextName] = useState(element.name ?? '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const typeaheadRef = useRef<HTMLDivElement>(null);
  useDismissOnOutsideClick(typeaheadRef, showSuggestions, () => setShowSuggestions(false));
  const inputRef = useRef<HTMLInputElement>(null);

  const hasInverse = element.type === 'radio';
  const { data: suggestions = [] } = useTagSuggestions(projectId, tagInput, hasInverse);

  const handleEdit = () => {
    setTagInput('');
    setSelectedTag(element.tag ?? null);
    setTextName(element.name ?? '');
    setEditMode(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSave = () => {
    if (element.type === 'text') {
      onSave({ ...element, name: textName });
    } else if (selectedTag) {
      onSave({ ...element, tagId: selectedTag.id, tag: selectedTag });
    }
    setEditMode(false);
  };

  const handleCancel = () => {
    if (isBlankElement(element)) {
      onRemove();
    } else {
      setEditMode(false);
    }
  };

  // Write relationships back onto element.tag so reopening the modal shows them.
  const handleTagEdited = ({ inverseObjects, childrenObjects, ...saved }: Tag & { inverseObjects: TagRef[]; childrenObjects: TagRef[] }) => {
    onSave({
      ...element,
      type: inverseObjects?.length ? 'radio' : 'checkbox',
      tagId: saved.id,
      tag: { ...element.tag, ...saved },
      inverseTag: inverseObjects?.[0] ?? undefined,
      children: childrenObjects as Tag[],
    });
    setShowEditModal(false);
    setEditMode(false);
  };

  const hasTag = element.tag && element.type !== 'text';

  if (editMode) {
    return (
      <>
        <div ref={ref} className="admin-tag-edit-form" style={{ marginBottom: 4, padding: '16px 12px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
          <form onSubmit={e => { e.preventDefault(); handleSave(); }}>
            {element.type === 'text' ? (
              <div className="input-group">
                <span className="input-group-addon">
                  <i className="fas fa-tag" /> <span>Custom</span>
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  autoComplete="off"
                  className="form-control"
                  placeholder="Name"
                  value={textName}
                  onChange={e => setTextName(e.target.value)}
                />
              </div>
            ) : (
              <>
                <div className="input-group" style={{ position: 'relative' }} ref={typeaheadRef}>
                  <span className="input-group-addon">
                    <i className={`far fa-fw ${hasInverse ? 'fa-dot-circle' : 'fa-check-square'}`} />
                    {' '}<span>{hasInverse ? 'Radio' : 'Checkbox'}</span>
                  </span>
                  <input
                    ref={inputRef}
                    type="text"
                    autoComplete="off"
                    className="form-control"
                    placeholder="Tag Name"
                    value={tagInput || (selectedTag?.name ?? '')}
                    onChange={e => { setTagInput(e.target.value); setSelectedTag(null); setShowSuggestions(true); }}
                    onFocus={() => setShowSuggestions(true)}
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <ul className="dropdown-menu" style={{ display: 'block', width: '100%', top: '100%' }}>
                      {suggestions.map((t: any) => (
                        <li key={t.id}>
                          <a href="#" onClick={e => { e.preventDefault(); setSelectedTag(t); setTagInput(''); setShowSuggestions(false); }}>
                            {t.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <p className="help-block">
                  {hasInverse ? 'Filtered by tags who have an inverse' : 'Filtered by tags who do not have an inverse'}
                </p>
              </>
            )}

            <div className="text-right" style={{ marginTop: 8 }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={element.type === 'text' ? !textName : !selectedTag}
              >
                Save
              </button>
              {' '}
              <button type="button" className="btn btn-default" onClick={handleCancel}>Cancel</button>
              {' '}
              {hasTag && (
                <button type="button" className="btn btn-default" onClick={() => setShowEditModal(true)}>
                  <i className="fas fa-pencil-alt" aria-hidden="true" /> Edit Tag
                </button>
              )}
              {' '}
              {(element.tag || (element.type === 'text' && element.name)) && (
                <button type="button" className="btn btn-danger" onClick={onRemove}>
                  <i className="fas fa-trash" aria-hidden="true" /> Remove
                </button>
              )}
            </div>
          </form>
        </div>

        {showEditModal && element.tag && (
          <TagModal
            projectId={projectId}
            tag={element.tag}
            onClose={() => setShowEditModal(false)}
            onSaved={handleTagEdited}
          />
        )}
      </>
    );
  }

  return (
    <div ref={ref} className="admin-tag-form-element" style={{ opacity: isDragging ? 0.4 : 1 }}>
      <div className="admin-tag-edit">
        <i className="fas fa-align-justify" style={{ verticalAlign: 'middle' }} aria-hidden="true" />
      </div>

      {element.type === 'radio' && (
        <div className="btn-group pull-left" style={{ paddingLeft: 10 }}>
          <button type="button" className="btn btn-sm btn-default">
            <i className="far fa-circle" aria-hidden="true" /> {element.tag?.name ?? '(tag)'}
          </button>
          {element.inverseTag && (
            <button type="button" className="btn btn-sm btn-default">
              <i className="far fa-circle" aria-hidden="true" /> {element.inverseTag.name}
            </button>
          )}
        </div>
      )}

      {element.type === 'checkbox' && (
        <div className="btn-group pull-left" style={{ paddingLeft: 10 }}>
          <button type="button" className="btn btn-sm btn-default">
            <i className="far fa-square" aria-hidden="true" /> {element.tag?.name ?? '(tag)'}
          </button>
        </div>
      )}

      {element.type === 'text' && (
        <div className="pull-left" style={{ display: 'table-cell', paddingTop: 5, paddingLeft: 10, verticalAlign: 'middle' }}>
          {element.name || '(custom text)'}
        </div>
      )}

      <div className="admin-tag-edit pull-right">
        <i className="fas fa-edit fa-lg" style={{ cursor: 'pointer' }} aria-hidden="true" onClick={handleEdit} />
      </div>
      <div style={{ clear: 'both' }} />
      {element.type !== 'text' && (
        <>
          <ChildPreview parentName={hasInverse ? element.tag?.name : undefined} tags={element.children} />
          {hasInverse && <ChildPreview parentName={element.inverseTag?.name} tags={element.inverseChildren} />}
        </>
      )}
    </div>
  );
}

// Mirrors the child checkboxes the viewer's TaggingForm shows once the parent is applied.
function ChildPreview({ parentName, tags }: { parentName?: string; tags?: Tag[] | null }) {
  if (!tags?.length) return null;
  return (
    <div className="admin-tag-form-element-children" style={{ paddingLeft: 40 }}>
      {parentName && <small className="text-muted">{parentName}:</small>}
      {[...tags].sort((a, b) => a.name.localeCompare(b.name)).map(child => (
        <button key={child.id} type="button" className="btn btn-sm btn-default">
          <i className="far fa-square" aria-hidden="true" /> {child.name}
        </button>
      ))}
    </div>
  );
}
